import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSetAtom } from 'jotai';
import { Button, InlineNotification, TextInput } from '@carbon/react';
import {
  ProvisionUploadForm,
  type ProvisionPayload,
  type ProvisionUploadFormHandle,
} from 'components/add-document-modal/provision-upload-form';
import request from 'lib/request';
import {
  createVestingDocumentAtom,
  provisionVestingDocumentAtom,
  vestingCandidatesUrl,
  vestingDocumentProvisionUrl,
  vestingDocumentsUrl,
  type VestingDocumentsHost,
} from './atoms';
import type {
  VestingCandidate,
  VestingCandidatesResponse,
  VestingContext,
} from './types';
import { DocumentCatalog, type CatalogFilter } from './document-catalog';
import styles from './vesting-documents.module.scss';

// The Link Item panel owns the shell and the one Save button and drives this through the
// handle — the contract ProvisionUploadSection has.
export interface VestingDocumentHandle {
  submit: () => void;
}

// Both date refusals name `as_of` — missing (422) and unparseable (400) — and both want the
// same thing from the user. The error sentence is the API's only failure channel.
const asksForADate = (message: string): boolean => message.includes('as_of');

interface Props {
  host: VestingDocumentsHost;
  context?: VestingContext;
  /** A row is selected, or an upload is ready. */
  onReadyChange: (ready: boolean) => void;
  onPendingChange: (pending: boolean) => void;
  onSaved: () => void;
  /** Link several documents in one save. */
  multiple?: boolean;
}

// Conveyances, the tract's survey area, the deal's buyer, and the county only when there is no
// survey area. The server refuses any other type as a vesting document.
const catalogFilters = (context?: VestingContext): CatalogFilter[] => {
  const filters: CatalogFilter[] = [
    {
      key: 'conveyances',
      label: 'Conveyances',
      hint: 'conveyances only',
      params: { documentTypes: ['courthouse', 'non_courthouse'] },
    },
  ];
  if (context?.referenceLocationId)
    filters.push({
      key: 'survey-area',
      label: context.referenceLocationName ?? 'Survey area',
      hint: 'the survey area',
      params: { withinReferenceLocationId: context.referenceLocationId },
    });
  else if (context?.county)
    filters.push({
      key: 'county',
      label: context.county.name,
      hint: 'the county',
      params: { countyIds: [context.county.id] },
    });
  if (context?.buyerEntity)
    filters.push({
      key: 'buyer',
      label: `Granted to ${context.buyerEntity.name}`,
      hint: 'the buyer',
      params: { conveyeeIds: [context.buyerEntity.id] },
    });
  return filters;
};

export const VestingDocumentSection = forwardRef<VestingDocumentHandle, Props>(
  (
    { host, context, onReadyChange, onPendingChange, onSaved, multiple },
    ref
  ) => {
    const basePath = host.basePath;
    const acquisitionEffectiveDate = context?.acquisitionEffectiveDate ?? null;
    const createRow = useSetAtom(createVestingDocumentAtom);
    const provisionRow = useSetAtom(provisionVestingDocumentAtom);
    const formRef = useRef<ProvisionUploadFormHandle>(null);

    const [uploading, setUploading] = useState(false);
    // Undefined when the finder gave no answer, so the catalog claims no ranking.
    const [candidates, setCandidates] = useState<
      VestingCandidate[] | undefined
    >([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [needsDate, setNeedsDate] = useState(false);
    const [asOf, setAsOf] = useState<string>(acquisitionEffectiveDate ?? '');
    // Pre-fills, not search text: free text is AND-matched, so area, party and county together would match almost nothing.
    const filters = catalogFilters(context);
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>(
      []
    );
    const [uploadReady, setUploadReady] = useState(false);
    const [pending, setPending] = useState(false);
    // `pending` lags a render, so a fast double-click would file the same document twice.
    const pendingRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    // Only the latest search may land; an earlier date's answer arriving late is stale.
    const searchRef = useRef(0);

    const loadCandidates = useCallback(
      (date?: string) => {
        const search = ++searchRef.current;
        const stale = () => search !== searchRef.current;
        setCandidatesLoading(true);
        setError(null);
        request
          .get<VestingCandidatesResponse>(
            vestingCandidatesUrl({ basePath }, date || null)
          )
          .then((res) => {
            if (stale()) return;
            if ('error' in res) {
              setCandidates(undefined);
              // A date refusal is a question; "no suggestions" would read as "no such deed".
              if (asksForADate(res.error)) setNeedsDate(true);
              else setError(res.error);
              return;
            }
            setCandidates(res.candidates ?? []);
            setNeedsDate(false);
          })
          .catch(() => {
            if (stale()) return;
            setCandidates(undefined);
            setError('Failed to load suggested documents.');
          })
          .finally(() => {
            if (!stale()) setCandidatesLoading(false);
          });
      },
      [basePath]
    );

    useEffect(() => {
      loadCandidates(acquisitionEffectiveDate ?? undefined);
      return () => {
        searchRef.current += 1;
      };
    }, [acquisitionEffectiveDate, loadCandidates]);

    useEffect(() => {
      onReadyChange(uploading ? uploadReady : selectedDocumentIds.length > 0);
    }, [onReadyChange, uploading, uploadReady, selectedDocumentIds]);

    const settle = (message?: string) => {
      pendingRef.current = false;
      setPending(false);
      onPendingChange(false);
      if (message) setError(message);
      else onSaved();
    };

    const start = () => {
      pendingRef.current = true;
      setPending(true);
      onPendingChange(true);
      setError(null);
    };

    const link = (documentId: number) =>
      new Promise<void>((resolve, reject) =>
        createRow({
          url: vestingDocumentsUrl({ basePath }),
          method: 'POST',
          data: { vesting_document: { documentId } },
          onSuccess: () => {
            // A retry after a partial failure links only what is left.
            setSelectedDocumentIds((ids) =>
              ids.filter((id) => id !== documentId)
            );
            resolve();
          },
          onError: (message?: string) =>
            reject(new Error(message ?? 'Failed to link the document.')),
        })
      );

    // One at a time: each link can reorder the tract's preferred document.
    const handleCreate = async () => {
      if (!selectedDocumentIds.length || pendingRef.current) return;
      start();
      try {
        for (const documentId of selectedDocumentIds) await link(documentId);
        settle();
      } catch (failure) {
        settle((failure as Error).message);
      }
    };

    const handleProvision = (payload: ProvisionPayload) => {
      if (pendingRef.current) return;
      start();
      provisionRow({
        url: vestingDocumentProvisionUrl({ basePath }),
        method: 'POST',
        data: { vesting_document: { provision: payload } },
        onSuccess: () => settle(),
        onError: (message?: string) =>
          settle(message ?? 'Failed to upload and provision the document.'),
      });
    };

    useImperativeHandle(ref, () => ({
      submit: () => (uploading ? formRef.current?.submit() : handleCreate()),
    }));

    return (
      <div className={styles.panelBody}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <p className={styles.panelIntro}>
          Find the recorded conveyance that created this tract — the deed that
          brought it into the CRM.
        </p>

        {uploading ? (
          <>
            <Button kind="ghost" size="sm" onClick={() => setUploading(false)}>
              Back to the catalog
            </Button>
            <ProvisionUploadForm
              ref={formRef}
              // The DocumentCreator selector, not the STI class and not the recording vocabulary.
              documentType="courthouse"
              idPrefix="vesting-provision-"
              onReadyChange={setUploadReady}
              onSubmit={handleProvision}
              submitting={pending}
              error={null}
              onDismissError={() => setError(null)}
            />
            <p className={styles.uploadNote}>
              Reading the deed takes a few minutes. The row appears immediately
              as
              <strong> Processing</strong> — with no volume, page or recorded
              date — and fills in when extraction lands.
            </p>
          </>
        ) : (
          <>
            {needsDate && (
              <>
                <InlineNotification
                  kind="info"
                  lowContrast
                  hideCloseButton
                  title="A date is needed"
                  subtitle="This tract has no acquisition date, so the search needs one to look around."
                />
                {/* Native date input: it yields the YYYY-MM-DD Date.iso8601 parses; looser is a 400. */}
                <TextInput
                  id="vesting-candidates-as-of"
                  type="date"
                  labelText="Search around this date"
                  value={asOf}
                  size="sm"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setAsOf(event.target.value)
                  }
                  onBlur={() => asOf && loadCandidates(asOf)}
                />
              </>
            )}

            <DocumentCatalog
              candidates={candidates}
              candidatesLoading={candidatesLoading}
              filters={filters}
              multiple={multiple}
              selectedDocumentIds={selectedDocumentIds}
              onSelect={setSelectedDocumentIds}
            />

            <Button kind="ghost" size="sm" onClick={() => setUploading(true)}>
              Not in the corpus? Upload the deed instead
            </Button>
          </>
        )}
      </div>
    );
  }
);

VestingDocumentSection.displayName = 'VestingDocumentSection';
